/**
 * Login Dice Scene
 * Lightweight Three.js scene showing 3 stacked dice (DSS01 model)
 * with a gently panning spotlight for subtle color shifts.
 *
 * Uses the SharedRenderer singleton instead of creating its own WebGL context.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { SharedRenderer } from '../../engine/render/SharedRenderer';
import { prewarmShaders } from '../../engine/render/precompile';

const DICE_GLB_PATH = '/assets/models/dice/DSS01.glb';
const DICE_SCALE = 0.53;
const SPOT_PAN_RADIUS = 1.0;
const SPOT_PAN_SPEED = 0.28;
const SPOT_PAN_Y_RATIO = 0.1;

// Width-driven camera framing. The container is `flex: 1` (leftover height),
// and a fixed vertical FOV makes the dice's on-screen size proportional to
// container HEIGHT — so on 360px-wide mid-range Samsungs (short leftover area)
// the stack rendered visibly smaller relative to the phone width than on wider
// devices. Instead, dolly the camera along its original view axis every time
// the aspect changes so the stack always spans WIDTH_FRACTION of the view
// width, clamped so the top die never clips on short/wide containers.
const CAM_LOOK_AT = new THREE.Vector3(0, 0.6, 0);
const CAM_DIR = new THREE.Vector3(0, 2.9, 6).normalize(); // original (0,3.5,6) minus look-at
const STACK_WIDTH = 2.1;      // dice at x ±0.52, each ~1u wide, plus slight yaw
const WIDTH_FRACTION = 0.5;   // ≈ the original framing on a 393px-wide device
const STACK_HALF_EXTENT_Y = 1.7; // look-at → above top die, with shadow breathing room


export class LoginDiceScene {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private diceGroup: THREE.Group;
  private spotlight: THREE.SpotLight;
  private clock: THREE.Clock;
  private viewId: string | null = null;
  private envRT: THREE.WebGLCubeRenderTarget | null = null;
  private shared: SharedRenderer;
  private lastAspect = 0;
  public readonly ready: Promise<void>;

  constructor(private container: HTMLElement) {
    this.shared = SharedRenderer.instance;

    this.scene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(35, 1, 0.1, 50);
    this.camera.position.copy(CAM_LOOK_AT).addScaledVector(CAM_DIR, 6.67); // pre-fit fallback ≈ old (0, 3.5, 6)
    this.camera.lookAt(CAM_LOOK_AT);

    // Dice group (populated async)
    this.diceGroup = new THREE.Group();
    this.scene.add(this.diceGroup);

    // Shadow floor
    const floorGeo = new THREE.PlaneGeometry(12, 12);
    const floorMat = new THREE.ShadowMaterial({ opacity: 0.35 });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.01;
    floor.receiveShadow = true;
    this.scene.add(floor);

    // Stationary spotlight
    this.spotlight = new THREE.SpotLight(0xffeedd, 80, 20, (32 * Math.PI) / 180, 0.5, 1.5);
    this.spotlight.position.set(1.6, 5, 5.1);
    this.spotlight.target.position.set(0, 0.5, 0);
    this.spotlight.castShadow = true;
    this.spotlight.shadow.mapSize.set(512, 512);
    this.spotlight.shadow.radius = 4;
    // The renderer uses VSM shadows (smooth but precision-sensitive). Bracket the
    // shadow camera tightly around the dice (~7u from this light) instead of the
    // 0.5/500 default — a wide range bands VSM — and add the VSM bias/blur knobs
    // so the login dice shadow stays clean (no floor banding, no light-bleed).
    this.spotlight.shadow.camera.near = 2;
    this.spotlight.shadow.camera.far = 16;
    this.spotlight.shadow.blurSamples = 8;
    this.spotlight.shadow.bias = -0.0008;
    this.spotlight.shadow.normalBias = 0.05;
    this.scene.add(this.spotlight);
    this.scene.add(this.spotlight.target);

    // Top fill light
    const topLight = new THREE.DirectionalLight(0xffffff, 0.6);
    topLight.position.set(0, 6, 0);
    this.scene.add(topLight);

    // Ambient
    const ambient = new THREE.AmbientLight(0xffffff, 0.25);
    this.scene.add(ambient);

    // Env map for reflections — rendered into an RGBA8 cube render target
    // (UnsignedByteType), NOT a PMREM HalfFloat render target. HalfFloat RTs are
    // the VRAM class that blows the WKWebView budget; login runs first in the
    // new-user flow, so keep it cheap. One CubeCamera render of the env-scene.
    const envScene = new THREE.Scene();
    envScene.background = new THREE.Color(0x080810);
    const envLight1 = new THREE.PointLight(0x4422aa, 3, 20);
    envLight1.position.set(-5, 3, -5);
    envScene.add(envLight1);
    const envLight2 = new THREE.PointLight(0x002244, 2, 20);
    envLight2.position.set(5, 2, 5);
    envScene.add(envLight2);
    const envLight3 = new THREE.PointLight(0x331111, 2, 20);
    envLight3.position.set(0, 5, 0);
    envScene.add(envLight3);
    this.envRT = new THREE.WebGLCubeRenderTarget(128, { type: THREE.UnsignedByteType });
    new THREE.CubeCamera(0.1, 100, this.envRT).update(this.shared.gl, envScene);
    this.scene.environment = this.envRT.texture;

    this.clock = new THREE.Clock();

    // Register as a view on the shared renderer
    this.viewId = this.shared.addView({
      scene: this.scene,
      camera: this.camera,
      container: this.container,
      priority: 10, // Higher priority = renders on top when multiple views exist
      onBeforeRender: () => this.update(),
    });

    // Load dice model
    this.ready = this.loadDice();
  }

  private async loadDice(): Promise<void> {
    const loader = new GLTFLoader();

    try {
      const gltf = await loader.loadAsync(DICE_GLB_PATH);
      const template = gltf.scene;

      const box = new THREE.Box3().setFromObject(template);
      const center = box.getCenter(new THREE.Vector3());
      template.position.sub(center);
      template.scale.setScalar(DICE_SCALE);

      template.traverse((node) => {
        if (!(node as THREE.Mesh).isMesh) return;
        const mesh = node as THREE.Mesh;
        mesh.castShadow = true;
        mesh.receiveShadow = true;

        const mat = mesh.material as THREE.MeshStandardMaterial;
        if (mat.name === 'body') {
          mat.color.set(0x8b0000);
          mat.roughness = 0;
          mat.metalness = 0.1;
          mat.envMapIntensity = 1.5;
        } else if (mat.name === 'circles') {
          mat.color.set(0xffffff);
          mat.roughness = 0;
          mat.emissive.set(0xffffff);
          mat.emissiveIntensity = 1;
          mat.envMapIntensity = 0.5;
        }
      });

      const unitSize = 1.0;

      // Die 1 (bottom-left): show 6 front
      const die1 = template.clone(true);
      die1.position.set(-0.52, unitSize / 2, 0.2);
      die1.rotation.y = 0.15;
      die1.children[0].rotation.x = -Math.PI;
      die1.children[0].rotation.y = Math.PI / 2;
      this.diceGroup.add(die1);

      // Die 2 (bottom-right): show 5 front
      const die2 = template.clone(true);
      die2.position.set(0.52, unitSize / 2, 0.2);
      die2.rotation.y = -0.1;
      die2.children[0].rotation.x = Math.PI / 2;
      die2.children[0].rotation.y = -Math.PI / 2;
      this.diceGroup.add(die2);

      // Die 3 (top): show 4 front
      const die3 = template.clone(true);
      die3.position.set(0, unitSize / 2 + unitSize * 0.95, -0.15);
      die3.rotation.y = 0.3;
      die3.rotation.z = 0.05;
      die3.children[0].rotation.x = -Math.PI / 2;
      this.diceGroup.add(die3);

      // Warm the login-dice shaders before the scene first renders — same
      // WKWebView context-loss guard, and this is the first 3D the player sees.
      await prewarmShaders(this.scene, this.camera, this.scene);
    } catch (err) {
      console.warn('[LoginDiceScene] Failed to load dice GLB:', err);
    }
  }

  private update(): void {
    // SharedRenderer runs resizeIfNeeded (which sets camera.aspect) BEFORE
    // onBeforeRender, so the aspect is current here — no DOM reads needed.
    this.fitCameraToWidth();

    const elapsed = this.clock.getElapsedTime();

    // Gentle spotlight pan
    const angle = elapsed * SPOT_PAN_SPEED;
    this.spotlight.position.set(
      1.6 + Math.sin(angle) * SPOT_PAN_RADIUS,
      5 + Math.cos(angle * 0.7) * SPOT_PAN_RADIUS * SPOT_PAN_Y_RATIO,
      5.1,
    );
  }

  /** Dolly the camera so the dice stack spans WIDTH_FRACTION of the view width. */
  private fitCameraToWidth(): void {
    const aspect = this.camera.aspect;
    if (aspect === this.lastAspect || !(aspect > 0)) return;
    this.lastAspect = aspect;

    const tanHalfV = Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2));
    // Distance where the stack occupies WIDTH_FRACTION of the horizontal view...
    let dist = STACK_WIDTH / (2 * WIDTH_FRACTION * tanHalfV * aspect);
    // ...but never so close that the stack overflows the view vertically.
    dist = Math.max(dist, STACK_HALF_EXTENT_Y / tanHalfV);

    this.camera.position.copy(CAM_LOOK_AT).addScaledVector(CAM_DIR, dist);
    this.camera.lookAt(CAM_LOOK_AT);
  }

  dispose(): void {
    // Remove from shared renderer
    if (this.viewId) {
      this.shared.removeView(this.viewId);
      this.viewId = null;
    }

    // Dispose the env cube render target (frees its texture + framebuffer).
    this.envRT?.dispose();
    this.envRT = null;
    this.scene.environment = null;

    // Free mesh geometry, material TEXTURES (material.dispose() does NOT free
    // maps) and the spotlight SHADOW MAP (a render target — the old traverse
    // only handled Mesh, so the 512² shadow RT + the dice textures leaked into
    // the session and rode through the tutorial/game on the tight iOS budget).
    this.scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (mesh.isMesh) {
        mesh.geometry?.dispose();
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        for (const m of mats) {
          if (!m) continue;
          for (const key in m) {
            const v = (m as unknown as Record<string, unknown>)[key] as { isTexture?: boolean; dispose?: () => void } | null;
            if (v && v.isTexture && typeof v.dispose === 'function') v.dispose();
          }
          m.dispose();
        }
      }
      const light = obj as THREE.Light & { shadow?: THREE.LightShadow };
      if (light.isLight && light.shadow?.map) {
        light.shadow.map.dispose();
        light.shadow.map = null as unknown as THREE.WebGLRenderTarget;
      }
    });
  }
}
